import { createSelector } from 'reselect';
import { List } from 'immutable';
import { ALARMS } from '../constants/alarm.constants';
import { initialState } from '../reducers/alarm.reducer';
import { groupAlarmSituations, isAssistanceRequestAlarm, situationOf } from '../../utils/models/v4AlarmSituation';
import { selectState as selectAppState } from './app.selectors';
import { v4RequestsOnOwnPage } from '../../containers/AlarmPage/functions';

const selectState = state => state[ALARMS] || initialState;
// Read at call time: app.selectors sits in an import cycle (via configureStore) with this module.
const selectUser = state => selectAppState(state).get('currentUser');

// A V4 device linked to a V3 ARC raises its alarm in BOTH stacks. The V3 copy is
// a passthrough that names the V4 original in `payload.alarm_uuid` — a JSON
// *string* on the wire, hence the parse. Null when there's no such link, which
// means it's a real V3 alarm.
const v4AlarmUuidOf = (alarm) => {
	const payload = alarm && alarm.get('payload');
	if (!payload) return null;
	if (typeof payload !== 'string') return payload.alarm_uuid || null;
	try {
		return JSON.parse(payload).alarm_uuid || null;
	} catch (e) {
		return null;
	}
};

// V3 alarm ids are integers, V4 are UUID strings — they never collide, so we
// concat without dedup. The V3 copy of a V4 alarm we hold is dropped: a passthrough
// whose `alarm_uuid` names it, or the V3 alarm it names as `v3AlarmId`. Only while
// the V4 alarm is held, so a failed refetch never hides an alarm.
// Sorting by `-date` keeps newest first across both.
// `v4ClosedAlarms` (lazily fetched on expand) is folded in too; an id in both V4
// lists is a lagging open row against a fresh closed one — V4 alarms never reopen
// (`Alarm.canOpen` is false for v4) — so the closed copy wins.
const mergeAlarmLists = (data, v4Open, v4ClosedList) => {
	const v4 = v4Open || List();
	const v4Closed = v4ClosedList || List();
	const closedIds = new Set(v4Closed.map(a => a.id));

	// V4 alarms we hold, open or closed, and the V3 alarms they name.
	const v4Ids = new Set();
	const v3TwinIds = new Set();
	v4.concat(v4Closed).forEach(a => {
		v4Ids.add(String(a.id));
		if (a.get('v3AlarmId')) v3TwinIds.add(String(a.get('v3AlarmId')));
	});
	const all = data.toList();
	const v3 = v4Ids.size
		? all.filter(a => !v4Ids.has(v4AlarmUuidOf(a)) && !v3TwinIds.has(String(a.id)))
		: all;

	return v3.concat(v4.filter(a => !closedIds.has(a.id))).concat(v4Closed).sortBy(a => -a.get('date'));
};

const mergedAlarms = (state) =>
	mergeAlarmLists(state.get('data'), state.get('v4Alarms'), state.get('v4ClosedAlarms'));

// Keyed on the three lists only, so unrelated alarm state does not regroup every situation.
const selectMergedAlarmLists = createSelector(
	[
		state => selectState(state).get('data'),
		state => selectState(state).get('v4Alarms'),
		state => selectState(state).get('v4ClosedAlarms'),
	],
	mergeAlarmLists
);
const selectSituations = createSelector(selectMergedAlarmLists, groupAlarmSituations);

export const selectAlarms = () => createSelector(selectState, mergedAlarms);
export const selectAlarmsFetched = () => createSelector(selectState, state => state.get('fetched'));
export const selectOpenAlarms = () => createSelector(selectState, state => mergedAlarms(state).filter(a => a.isOpen));
// Alerts, its badge, siren and title: a V3 user's V4 assistance requests count on their own page instead.
export const selectOpenAlertAlarms = () => createSelector(
	[selectOpenAlarms(), selectUser],
	(alarms, user) => (v4RequestsOnOwnPage(user) ? alarms.filter(a => !isAssistanceRequestAlarm(a)) : alarms)
);
export const selectWizard = () => createSelector(selectState, state => state.get('wizard'));
export const selectAlarmSoundEnabled = () => createSelector(selectState, state => !!state.get('alarmSoundEnabled'));
export const selectAlertSoundEnabled = () => createSelector(selectState, state => !!state.get('alertSoundEnabled'));
export const selectAssistanceRequestSoundEnabled = () => createSelector(selectState, state => !!state.get('assReqSoundEnabled'));
export const selectSafetyRegions = () => createSelector(selectState, state => state.get('safetyRegions'));
export const selectDetailedAlarm = () => createSelector(selectState, state => state.get('detailedAlarm'));
const rtcRoomOf = alarm => !alarm ? null : (
	alarm.get?.('rtcRoom') ||
	alarm.get?.('rtc_room') ||
	alarm.rtcRoom ||
	alarm.rtc_room ||
	alarm.get?.('v4Data')?.rtc_room ||
	null
);
export const selectRtcRoomBySubject = subject => createSelector(selectState, state => {
	if (!subject) return null;
	const matches = alarm => alarm && `alarm_${alarm.get?.('id') ?? alarm.id}` === subject;
	const detailed = state.get('detailedAlarm');
	const detailedRoom = matches(detailed) ? rtcRoomOf(detailed) : null;
	if (detailedRoom) return detailedRoom;
	const alarm = mergedAlarms(state).find(matches);
	return rtcRoomOf(alarm);
});
export const selectLoadingDetailedAlarm = () => createSelector(selectState, state => state.get('loadingDetailedAlarm'));
export const selectRecording = () => createSelector(selectState, state => state.get('recording'));
export const selectShowARCAlarms = () => createSelector(selectState, state => state.get('showARCAlarms'));
export const selectV4ClosedAlarms = () => createSelector(selectState, state => state.get('v4ClosedAlarms') || List());
export const selectV4ClosedAlarmsFetching = () => createSelector(selectState, state => !!state.get('v4ClosedAlarmsFetching'));
export const selectV4ClosedAlarmsFetched = () => createSelector(selectState, state => !!state.get('v4ClosedAlarmsFetched'));
export const selectAlarmLocationView = () => createSelector(selectState, state => state.get('alarmLocationView'));
export const selectLatestAlarmPosition = () => createSelector(selectState, state => state.get('latestAlarmPosition'));

// Shared instances, so the memo holds across components.
export const selectMergedAlarmList = () => selectMergedAlarmLists;
export const selectAlarmSituations = () => selectSituations;
export const selectOpenAlarmSituations = () => createSelector(selectSituations, situations => situations.filter(s => s.isOpen));
export const selectSituationOfDetailedAlarm = () => createSelector(
	[selectSituations, state => selectState(state).get('detailedAlarm')],
	(situations, detailed) => (detailed ? situationOf(situations, detailed.get('id')) : null)
);
